import React, { useState, useEffect } from "react";
import { Check, ArrowRight, Infinity as InfinityIcon, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { PRICING, CHECKOUT_URLS, CHECKOUT_FALLBACK, SUPPORT_EMAIL } from "../lib/constants";

export const Pricing = () => {
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-16 sm:py-24 lg:py-32 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-10 sm:mb-14">
          <div className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 mb-4">
            [ 04 — Pricing ]
          </div>
          <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl font-semibold leading-[0.95] text-white">
            One battery replacement
            <br />
            pays for{" "}
            <span className="brand-text-gradient">many years</span> of EVIQ.
          </h2>
          <p className="text-zinc-400 mt-5 sm:mt-6 text-base sm:text-lg max-w-xl leading-relaxed">
            A Tesla pack replacement runs $15,000–$22,000 out of pocket. EVIQ is{" "}
            <span className="text-white">$9.99/month</span>,{" "}
            <span className="text-white">$99.99/year</span>, or a{" "}
            <span className="text-white">$199.99 one-time</span> lifetime purchase.
            Every plan starts with a <span className="text-white">7-day free preview</span>.
            Card required — cancel by email in one line.
          </p>
          <div
            className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-[12px] text-zinc-400"
            data-testid="pricing-guarantee"
          >
            {[
              "7-day free preview on every plan",
              "Cancel in one line by email",
              "Not satisfied? Your first month is on us",
            ].map((g) => (
              <span key={g} className="inline-flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5 shrink-0" strokeWidth={2.5} style={{ color: "#39FF14" }} />
                {g}
              </span>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 max-w-7xl mx-auto gap-4 lg:gap-6">
          {PRICING.map((p, i) => {
            const url = CHECKOUT_URLS[p.plan];
            const hasUrl = !!url;
            return (
            <div
              key={p.name}
              data-testid={`pricing-card-${p.plan}`}
              className={`relative rounded-3xl p-6 sm:p-8 lg:p-10 flex flex-col ${
                p.highlight
                  ? "gradient-border bg-[#0a0a0a]"
                  : "border border-white/10 bg-[#0a0a0a]"
              }`}
            >
              {p.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="brand-gradient text-black font-mono text-[10px] tracking-[0.2em] uppercase font-semibold px-3 py-1 rounded-full">
                    Most popular
                  </span>
                </div>
              )}
              {p.lifetime && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase font-semibold px-3 py-1 rounded-full border border-[#00E5FF]/50 bg-[#00E5FF]/[0.08] text-[#00E5FF] inline-flex items-center gap-1.5">
                    <InfinityIcon className="w-3 h-3" strokeWidth={2.5} />
                    Pay once
                  </span>
                </div>
              )}
              <div className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
                {p.name}
              </div>
              <div className="mt-5 flex items-baseline gap-1">
                <span className="font-display text-5xl font-semibold text-white">
                  {p.price}
                </span>
                <span className="text-sm text-zinc-500 font-mono">{p.period}</span>
              </div>
              {p.savings && (
                <div
                  className="mt-2 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border"
                  style={{
                    color: "#39FF14",
                    borderColor: "rgba(57,255,20,0.3)",
                    background: "rgba(57,255,20,0.06)",
                  }}
                  data-testid={`plan-savings-${p.plan}`}
                >
                  <span className="w-1 h-1 rounded-full bg-[#39FF14] shadow-[0_0_4px_#39FF14]" />
                  {p.savings}
                </div>
              )}
              <p className="text-zinc-400 mt-3 text-sm leading-relaxed min-h-[3rem]">
                {p.headline}
              </p>

              {/* Stripe hosted payment links — all three plans. Payment is
                  auto-verified via the /api/webhook/stripe-links webhook. */}
              {hasUrl ? (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`pricing-cta-${p.plan}`}
                  className={`mt-7 w-full rounded-full px-5 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 transition-all ${
                    p.highlight
                      ? "brand-gradient text-black hover:opacity-90"
                      : "border border-white/15 text-white hover:bg-white/5"
                  }`}
                >
                  {p.cta} · {p.price}
                  <ExternalLink className="w-3.5 h-3.5" strokeWidth={2.5} />
                </a>
              ) : (
                <Link
                  to={CHECKOUT_FALLBACK}
                  data-testid={`pricing-cta-${p.plan}`}
                  className="mt-7 w-full rounded-full px-5 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 border border-[#00E5FF]/40 text-white hover:bg-[#00E5FF]/[0.06] transition-all"
                >
                  Request lifetime access
                  <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} />
                </Link>
              )}

              <div className="mt-8 pt-8 border-t border-white/5 flex-1">
                <ul className="space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-zinc-300">
                      <Check
                        className="w-4 h-4 mt-0.5 shrink-0"
                        strokeWidth={2.5}
                        style={{ color: "#39FF14" }}
                      />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          );
          })}

          {/* One-off certified report for used-Tesla shoppers */}
          <SingleReportCard />
        </div>

        <p className="text-xs text-zinc-500 font-mono mt-8 text-center max-w-2xl mx-auto leading-relaxed">
          Secure checkout via Stripe. Your account unlocks automatically the moment your
          payment completes — no waiting, no manual verification. Cancel anytime in the app or by email to{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-zinc-300 hover:text-white underline underline-offset-2">{SUPPORT_EMAIL}</a>.
        </p>
      </div>
    </section>
  );
};

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/** 4th pricing card — buy a single certified report (no subscription).
 *  Shopper pastes the seller's passport link/ID + email → Stripe → PDF
 *  emailed instantly + download link appears here. */
const SingleReportCard = () => {
  const [phase, setPhase] = useState("idle"); // idle | form | waiting | paid
  const [passportRef, setPassportRef] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [purchaseId, setPurchaseId] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);

  useEffect(() => {
    if (phase !== "waiting" || !purchaseId) return undefined;
    const t = setInterval(async () => {
      try {
        const r = await fetch(`${API_BASE}/api/report/status/${purchaseId}`);
        if (!r.ok) return;
        const j = await r.json();
        if (j.paid) { setDownloadUrl(j.download_url); setPhase("paid"); }
      } catch { /* keep polling */ }
    }, 3500);
    return () => clearInterval(t);
  }, [phase, purchaseId]);

  const startCheckout = async (e) => {
    e.preventDefault();
    setError("");
    if (!passportRef.trim()) { setError("Paste the seller's EVIQ passport link or ID."); return; }
    if (!/.+@.+\..+/.test(email)) { setError("Enter a valid email address."); return; }
    setBusy(true);
    const w = window.open("about:blank", "_blank");
    try {
      const r = await fetch(`${API_BASE}/api/report/buy`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase(), passport_id: passportRef.trim() }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || "Could not start checkout — try again.");
      if (w) w.location = j.checkout_url; else window.open(j.checkout_url, "_blank", "noopener");
      setPurchaseId(j.purchase_id);
      setPhase("waiting");
    } catch (e2) {
      if (w) w.close();
      setError(e2.message || "Could not start checkout — try again.");
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid="pricing-card-report"
      className="relative rounded-3xl p-6 sm:p-8 lg:p-10 flex flex-col border border-[#FFD640]/25 bg-[#0a0a0a]"
    >
      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
        <span className="font-mono text-[10px] tracking-[0.2em] uppercase font-semibold px-3 py-1 rounded-full border border-[#FFD640]/50 bg-[#FFD640]/[0.08] text-[#FFD640]">
          For shoppers
        </span>
      </div>
      <div className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Single Report</div>
      <div className="mt-5 flex items-baseline gap-1">
        <span className="font-display text-5xl font-semibold text-white">$19.99</span>
        <span className="text-sm text-zinc-500 font-mono">one-time</span>
      </div>
      <p className="text-zinc-400 mt-3 text-sm leading-relaxed min-h-[3rem]">
        Buying a used Tesla? Get the certified battery report for any car with a shared EVIQ passport — no subscription.
      </p>

      {phase === "paid" ? (
        <div className="mt-7" data-testid="pricing-report-paid">
          <div className="text-white text-sm font-semibold mb-2">Report unlocked — check your email.</div>
          <a
            href={downloadUrl}
            data-testid="pricing-report-download"
            className="w-full rounded-full px-5 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 brand-gradient text-black hover:opacity-90 transition-all"
          >
            Download certified report
          </a>
        </div>
      ) : phase === "waiting" ? (
        <div className="mt-7 text-sm text-zinc-300" data-testid="pricing-report-waiting">
          <div className="font-semibold text-white mb-1">Complete payment in the Stripe tab.</div>
          <div className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-zinc-500">
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: "#00E5FF" }} />
            Watching for payment — your PDF is emailed instantly
          </div>
        </div>
      ) : phase === "form" ? (
        <form onSubmit={startCheckout} className="mt-7 space-y-2" data-testid="pricing-report-form">
          <input
            type="text"
            value={passportRef}
            onChange={(e) => setPassportRef(e.target.value)}
            placeholder="Seller's passport link or ID"
            data-testid="pricing-report-ref"
            disabled={busy}
            className="w-full rounded-lg border border-white/10 bg-black/60 px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#FFD640]/50"
          />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            data-testid="pricing-report-email"
            disabled={busy}
            className="w-full rounded-lg border border-white/10 bg-black/60 px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-[#FFD640]/50"
          />
          {error && <div className="text-[11px] text-[#FF6B57]" data-testid="pricing-report-error">{error}</div>}
          <button
            type="submit"
            disabled={busy}
            data-testid="pricing-report-submit"
            className="w-full rounded-full px-5 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 brand-gradient text-black hover:opacity-90 transition-all disabled:opacity-50"
          >
            {busy ? "Opening Stripe…" : "Pay $19.99 on Stripe"}
            {!busy && <ExternalLink className="w-3.5 h-3.5" strokeWidth={2.5} />}
          </button>
          <p className="text-[10px] text-zinc-500 leading-relaxed">
            No passport link? Ask the seller for their EVIQ passport URL — every listing badge and shared passport includes it.
          </p>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setPhase("form")}
          data-testid="pricing-cta-report"
          className="mt-7 w-full rounded-full px-5 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 border border-[#FFD640]/40 text-white hover:bg-[#FFD640]/[0.06] transition-all"
        >
          Get a single report · $19.99
          <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} />
        </button>
      )}

      <div className="mt-8 pt-8 border-t border-white/5 flex-1">
        <ul className="space-y-3">
          {[
            "2-page certified battery report (PDF)",
            "Cryptographically signed + verifiable",
            "Emailed instantly after payment",
            "EVIQ Estimated SoH, warranty position, risk",
          ].map((f) => (
            <li key={f} className="flex items-start gap-3 text-sm text-zinc-300">
              <Check className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2.5} style={{ color: "#FFD640" }} />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
